import type { AtmosphereQuality } from "./atmosphere.js";
import type { Camera } from "./camera.js";
import type { Renderer } from "./renderer.js";

export const CONTACT_SHADOW_CAPACITY = 64;
const VERTICES_PER_ACTOR = 9;
const FLOATS_PER_VERTEX = 6;
const INDICES_PER_ACTOR = 24;
const GROUND_OFFSET = 0.035;

/** Fixed shared storage: nine terrain samples per actor, eight triangles, no shadow textures. */
export class ContactShadowBatch {
  readonly vertices = new Float32Array(CONTACT_SHADOW_CAPACITY * VERTICES_PER_ACTOR * FLOATS_PER_VERTEX);
  readonly indices = new Uint16Array(CONTACT_SHADOW_CAPACITY * INDICES_PER_ACTOR);
  count = 0;

  constructor() {
    let offset = 0;
    for (let actor = 0; actor < CONTACT_SHADOW_CAPACITY; actor += 1) {
      for (let row = 0; row < 2; row += 1) {
        for (let column = 0; column < 2; column += 1) {
          const top = actor * VERTICES_PER_ACTOR + row * 3 + column;
          for (const index of [top, top + 3, top + 1, top + 1, top + 3, top + 4]) this.indices[offset++] = index;
        }
      }
    }
  }

  reset(): void { this.count = 0; }

  add(heightAt: (x: number, z: number) => number, x: number, y: number, z: number, baseRadius = 0.58, fadeHeight = 3, distanceOpacity = 1): boolean {
    if (this.count >= CONTACT_SHADOW_CAPACITY || !Number.isFinite(x + y + z + baseRadius + fadeHeight + distanceOpacity)
      || baseRadius <= 0 || fadeHeight <= 0) return false;
    const ground = heightAt(x, z);
    if (!Number.isFinite(ground)) return false;
    const height = Math.max(0, y - ground);
    const fade = Math.max(0, 1 - height / fadeHeight);
    const opacity = 0.34 * fade * fade * Math.max(0, Math.min(1, distanceOpacity));
    if (opacity < 0.006) return false;
    const radius = baseRadius * (1 + Math.min(1.2, height * 0.16));
    let offset = this.count * VERTICES_PER_ACTOR * FLOATS_PER_VERTEX;
    for (let row = -1; row <= 1; row += 1) {
      for (let column = -1; column <= 1; column += 1) {
        const pointX = x + column * radius;
        const pointZ = z + row * radius;
        const pointY = column === 0 && row === 0 ? ground : heightAt(pointX, pointZ);
        if (!Number.isFinite(pointY)) return false;
        this.vertices[offset++] = pointX;
        this.vertices[offset++] = pointY + GROUND_OFFSET;
        this.vertices[offset++] = pointZ;
        this.vertices[offset++] = column;
        this.vertices[offset++] = row;
        this.vertices[offset++] = opacity;
      }
    }
    this.count += 1;
    return true;
  }
}

const vertexSource = `#version 300 es
layout(location=0) in vec3 aPosition;
layout(location=1) in vec2 aOffset;
layout(location=2) in float aOpacity;
uniform mat4 uViewProjection;
out vec2 vOffset;
out float vOpacity;
void main() {
  vOffset = aOffset;
  vOpacity = aOpacity;
  gl_Position = uViewProjection * vec4(aPosition, 1.0);
}`;
const fragmentSource = `#version 300 es
precision highp float;
in vec2 vOffset;
in float vOpacity;
out vec4 outColor;
void main() {
  float radiusSquared = dot(vOffset, vOffset);
  float softness = exp(-2.8 * radiusSquared) * (1.0 - smoothstep(0.48, 1.0, radiusSquared));
  outColor = vec4(0.035, 0.045, 0.048, vOpacity * softness);
}`;

/** Collect after opaque actors, draw once before transparent effects. */
export class ActorContactShadows {
  private readonly batch = new ContactShadowBatch();
  private readonly program: WebGLProgram;
  private readonly vertexArray: WebGLVertexArrayObject;
  private readonly vertexBuffer: WebGLBuffer;
  private readonly indexBuffer: WebGLBuffer;
  private readonly viewProjection: WebGLUniformLocation | null;
  private camera: Camera | undefined;
  private maximumActors = CONTACT_SHADOW_CAPACITY;
  private maximumDistance = 60;
  private readonly bufferBytes = this.batch.vertices.byteLength + this.batch.indices.byteLength;

  constructor(private readonly renderer: Renderer, private readonly heightAt: (x: number, z: number) => number) {
    const gl = renderer.gl;
    this.program = createProgram(gl);
    const vertexArray = gl.createVertexArray(), vertexBuffer = gl.createBuffer(), indexBuffer = gl.createBuffer();
    if (!vertexArray || !vertexBuffer || !indexBuffer) throw new Error("Contact shadow buffer allocation failed");
    this.vertexArray = vertexArray;
    this.vertexBuffer = vertexBuffer;
    this.indexBuffer = indexBuffer;
    this.viewProjection = gl.getUniformLocation(this.program, "uViewProjection");
    gl.bindVertexArray(vertexArray);
    gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.batch.vertices.byteLength, gl.DYNAMIC_DRAW);
    for (const [location, width, offset] of [[0, 3, 0], [1, 2, 3], [2, 1, 5]]) {
      gl.enableVertexAttribArray(location!);
      gl.vertexAttribPointer(location!, width!, gl.FLOAT, false, FLOATS_PER_VERTEX * 4, offset! * 4);
    }
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, this.batch.indices, gl.STATIC_DRAW);
    gl.bindVertexArray(null);
    renderer.meshMemoryBytes += this.bufferBytes;
  }

  begin(camera: Camera, quality: AtmosphereQuality): void {
    this.camera = camera;
    this.batch.reset();
    this.maximumActors = quality === "low" ? 32 : quality === "medium" ? 48 : CONTACT_SHADOW_CAPACITY;
    this.maximumDistance = quality === "low" ? 30 : quality === "medium" ? 45 : 60;
  }

  addActor(x: number, y: number, z: number, radius = 0.58, fadeHeight = 3): void {
    const camera = this.camera;
    if (!camera || this.batch.count >= this.maximumActors) return;
    const distance = Math.hypot(x - camera.target[0], z - camera.target[2]);
    // Include the potential ground projection when an airborne actor itself is
    // above the frame; its contact can still be visible below it.
    if (distance >= this.maximumDistance || !camera.isSphereVisible(x, y - fadeHeight / 2, z, radius * 2.2 + fadeHeight / 2)) return;
    const distanceOpacity = Math.min(1, (this.maximumDistance - distance) / 10);
    this.batch.add(this.heightAt, x, y, z, radius, fadeHeight, distanceOpacity);
  }

  render(): void {
    if (!this.camera || this.batch.count === 0) return;
    const gl = this.renderer.gl;
    gl.useProgram(this.program);
    gl.bindVertexArray(this.vertexArray);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.batch.vertices, 0, this.batch.count * VERTICES_PER_ACTOR * FLOATS_PER_VERTEX);
    gl.uniformMatrix4fv(this.viewProjection, false, this.camera.viewProjection);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.depthMask(false);
    gl.drawElements(gl.TRIANGLES, this.batch.count * INDICES_PER_ACTOR, gl.UNSIGNED_SHORT, 0);
    gl.depthMask(true);
    gl.disable(gl.BLEND);
    this.renderer.metrics.drawCalls += 1;
    this.renderer.metrics.triangles += this.batch.count * 8;
    this.renderer.metrics.contactShadowDrawCalls += 1;
    this.renderer.metrics.contactShadowActors += this.batch.count;
    this.renderer.metrics.contactShadowTriangles += this.batch.count * 8;
  }

  dispose(): void {
    const gl = this.renderer.gl;
    gl.deleteVertexArray(this.vertexArray);
    gl.deleteBuffer(this.vertexBuffer);
    gl.deleteBuffer(this.indexBuffer);
    gl.deleteProgram(this.program);
    this.renderer.meshMemoryBytes -= this.bufferBytes;
  }
}

function createProgram(gl: WebGL2RenderingContext): WebGLProgram {
  const program = gl.createProgram();
  if (!program) throw new Error("Contact shadow program allocation failed");
  for (const [type, source] of [[gl.VERTEX_SHADER, vertexSource], [gl.FRAGMENT_SHADER, fragmentSource]] as const) {
    const shader = gl.createShader(type);
    if (!shader) throw new Error("Contact shadow shader allocation failed");
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader) ?? "Contact shadow shader compilation failed");
    gl.attachShader(program, shader);
    gl.deleteShader(shader);
  }
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program) ?? "Contact shadow shader link failed");
  return program;
}
